import type uPlot from "uplot";

import type { TelemetryAggregatePointDto } from "@/lib/api/types";

/** Build uPlot `AlignedData`: [unix_seconds[], y1[], y2[], ...] */
export function aggregatesToAlignedData(
  points: TelemetryAggregatePointDto[],
  metricKeys: string[],
): uPlot.AlignedData {
  const x: number[] = [];
  const ys: number[][] = metricKeys.map(() => []);

  for (const p of points) {
    x.push(new Date(p.t).getTime() / 1000);
    for (let i = 0; i < metricKeys.length; i++) {
      const key = metricKeys[i];
      if (!key) {
        continue;
      }
      const v = p.values[key];
      const row = ys[i];
      if (row) {
        row.push(v != null && typeof v === "number" && !Number.isNaN(v) ? v : NaN);
      }
    }
  }

  return [x, ...ys];
}

export type MutableAlignedBuffer = {
  x: number[];
  ys: number[][];
};

export function toMutableBuffer(aligned: uPlot.AlignedData, metricCount: number): MutableAlignedBuffer {
  const x = aligned[0] as number[];
  const ys: number[][] = [];
  for (let i = 0; i < metricCount; i++) {
    ys.push([...(aligned[i + 1] as number[])]);
  }
  return { x: [...x], ys };
}

export function alignedFromMutable(m: MutableAlignedBuffer): uPlot.AlignedData {
  return [m.x, ...m.ys];
}

export function trimBufferStart(m: MutableAlignedBuffer, maxLen: number): void {
  const overflow = m.x.length - maxLen;
  if (overflow <= 0) {
    return;
  }
  m.x.splice(0, overflow);
  for (const arr of m.ys) {
    arr.splice(0, overflow);
  }
}

/** uPlot x scale uses seconds (see chart `ms: 1e-3`). Slice to visible [xMin,xMax] + padding for fewer canvas draws. */
export function sliceBufferToXViewport(
  buf: MutableAlignedBuffer,
  xMin: number,
  xMax: number,
  pad: number,
): uPlot.AlignedData {
  const x = buf.x;
  const n = x.length;
  if (n === 0) {
    return [[], ...buf.ys.map(() => [])];
  }
  let lo = 0;
  let hi = n - 1;
  while (lo < n && (x[lo] as number) < xMin) {
    lo++;
  }
  while (hi > lo && (x[hi] as number) > xMax) {
    hi--;
  }
  lo = Math.max(0, lo - pad);
  hi = Math.min(n - 1, hi + pad);
  if (lo > hi) {
    const mid = Math.min(n - 1, Math.max(0, lo));
    const xs = x.slice(mid, mid + 1);
    const ys = buf.ys.map((row) => row.slice(mid, mid + 1));
    return [xs, ...ys];
  }
  const xs = x.slice(lo, hi + 1);
  const ys = buf.ys.map((row) => row.slice(lo, hi + 1));
  return [xs, ...ys];
}
